import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { Box } from "@mui/material";
import { keyframes } from "@mui/system";
import { tokens } from "../../../design/theme.next";

const settle = keyframes`
  0%   { opacity: 0; transform: translateY(-.55em) scale(.55); }
  55%  { opacity: 1; transform: translateY(.03em) scale(1.12, .9); }
  78%  { transform: translateY(-.02em) scale(.97, 1.03); }
  100% { opacity: 1; transform: none; }
`;

const breathe = keyframes`
  0%, 100% { transform: none; opacity: 1; }
  45%      { transform: scale(1.4); opacity: .55; }
`;

const reducedMotion = () =>
  typeof window !== "undefined" &&
  !!window.matchMedia &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

/**
 * The violet full stop in "Hugo.". It drops in and settles once on load.
 * While `thinking` it breathes, and when the work stops it finishes the
 * breath it is on. With reduced motion it simply sits there.
 */
export default function WordmarkDot({ color, settleIn = true, thinking = false }) {
  const [breathing, setBreathing] = useState(false);
  const thinkingRef = useRef(thinking);
  thinkingRef.current = thinking;

  useEffect(() => {
    if (thinking && !reducedMotion()) setBreathing(true);
    else if (!thinking && reducedMotion()) setBreathing(false);
  }, [thinking]);

  return (
    <Box
      component="span"
      sx={{
        display: "inline-block",
        color,
        transformOrigin: "50% 80%",
        animation: settleIn ? `${settle} 720ms ${tokens.a4.ease} 140ms both` : "none",
        "@media (prefers-reduced-motion: reduce)": { animation: "none" },
      }}
    >
      <Box
        component="span"
        data-breathing={breathing ? "true" : undefined}
        onAnimationIteration={() => {
          if (!thinkingRef.current) setBreathing(false);
        }}
        sx={{
          display: "inline-block",
          transformOrigin: "50% 82%",
          animation: breathing ? `${breathe} 900ms cubic-bezier(.4,0,.2,1) infinite` : "none",
          "@media (prefers-reduced-motion: reduce)": { animation: "none" },
        }}
      >
        .
      </Box>
    </Box>
  );
}

WordmarkDot.propTypes = { color: PropTypes.string.isRequired, settleIn: PropTypes.bool, thinking: PropTypes.bool };
